<template lang="pug">
.container
  search-and-create-box(:should-use-parent-create="true"
                        create-box-type="textarea"
                        :advanced="true"
                        :rows="3"
                        @search="onSearchChange"
                        @create="createNote")

  aiq-collapse.custom-notes(v-model="selectedCollapse")
    aiq-collapse-item(name="standard" :title="standardTitle")
      .notes_list
        custom-notes-item(v-for="item in standardNotes",
                          :note="item",
                          :key="item.index",
                          @editMode="editNoteItem",
                          @remove="deleteNote")
      infinite-loading(ref="standardInfiniteLoading"
                      :identifier="standardIdentifier"
                      v-if="doStandardInfiniteScroll"
                      class="text-center"
                      :distance="10"
                      @infinite="onStandardScrollLoad")
    aiq-collapse-item(v-if="showHeadsUp" name="heads-up" :title="headsUpTitle")
      .notes_list
        custom-notes-item(v-for="item in headsUpNotes",
                          :note="item",
                          :key="item.index",
                          @editMode="editNoteItem",
                          @remove="deleteNote")
      infinite-loading(ref="headsUpInfiniteLoading"
                      :identifier="headsUpIdentifier"
                      v-if="doHeadsUpfiniteScroll"
                      class="text-center"
                      :distance="10"
                      @infinite="onHeadsUpSrollLoad")
    .expired_toggler(v-if="featureFlags.NOTES_EXIPRATION")
      label {{ $t(`${baseTranslationPath}.show_expired_toggler.label`) }}
      aiq-switch(v-model="includeExpired"
                style="--aiq-switch-on-color: #30C28B; --aiq-switch-off-color: #C0CCDA")
  accordion-item-editor(v-if="isEditorVisible"
                        :item="selectedNote"
                        :is-visible="isEditorVisible"
                        toggle-property="is_heads_up"
                        :show-toggle="showHeadsUp"
                        :base-translation-path="baseTranslationPath"
                        :enable-expire-at="featureFlags.NOTES_EXIPRATION"
                        @save="updateNote"
                        @remove="deleteNote"
                        @close="closeEditor")
    template(#extra-fields="{ accordionItem, isBusy }" v-if="featureFlags.NOTES_EXIPRATION")
      .field-container.expire-date-row
        label {{ $t(`${baseTranslationPath}.fields.expire_date.title`) }}
        .date-picker-container
          aiq-date-picker(v-model="accordionItem.expire_date"
                          type="date"
                          :disabled="isBusy"
                          placeholder="Select a date"
                          :clearable="true"
                          :append-to-body="false"
                          class="normal-date-picker-popup")
</template>

<script>
import { mapState } from 'vuex';
import get from 'lodash/get';
import CustomNotesItem from './CustomNotesItem.vue';
import AccordionItemEditor from '@/components/AccordionItemEditor.vue';
import SearchAndCreateBox from '@/components/SearchAndCreateBox/SearchAndCreateBox.vue';
import { PAGINATION_DEFAULT } from '@/constants/pagination';
import { randomString } from '@/libs/stringUtils';

const SEARCH_PAGE_SIZE = 10;

export default {
  components: {
    CustomNotesItem,
    SearchAndCreateBox,
    AccordionItemEditor,
  },

  props: {
    conversation: {
      type: Object,
      default: () => ({}),
    },
  },

  async mounted() {
    await this.fetchNotes();
  },

  data() {
    return {
      selectedNote: null,
      isEditorVisible: false,
      selectedCollapse: ['standard'],
      standardNotesPagination: { ...PAGINATION_DEFAULT },
      headsUpNotesPagination: { ...PAGINATION_DEFAULT },
      standardNotes: [],
      headsUpNotes: [],
      baseTranslationPath: 'conversation_tab.side_panel.custom_notes_accordion',
      standardIdentifier: `standard_${randomString()}`,
      headsUpIdentifier: `heads-up_${randomString()}`,
      includeExpired: false,
    };
  },

  computed: {
    ...mapState({
      agentProfile: state => state.agent.profile,
      customer: state => state.conversations.selectedCustomer,
      featureFlags: state => state.featureFlags.FEATURE_FLAGS,
    }),
    doStandardInfiniteScroll() {
      return this.standardNotes.length < this.standardNotesPagination.rowCount;
    },
    doHeadsUpfiniteScroll() {
      return this.headsUpNotes.length < this.headsUpNotesPagination.rowCount;
    },
    standardTitle() {
      return `${this.$t(`${this.baseTranslationPath}.standard`)} (${this.standardNotesPagination.rowCount})`;
    },
    headsUpTitle() {
      return `${this.$t(`${this.baseTranslationPath}.heads_up`)} (${this.headsUpNotesPagination.rowCount})`;
    },
    showHeadsUp() {
      return this.canView('/conversations/right_panel/notes/heads_up');
    },
  },

  methods: {
    async fetchNotes(standardNotesArgs = [], headsUpNotesArgs = []) {
      await Promise.all([this.loadStandardNotes(...standardNotesArgs), this.loadHeadsUpNotes(...headsUpNotesArgs)]);
    },

    resetLists() {
      this.standardNotesPagination = { ...PAGINATION_DEFAULT };
      this.headsUpNotesPagination = { ...PAGINATION_DEFAULT };
      this.standardNotes = [];
      this.headsUpNotes = [];
      this.standardIdentifier = `standard_${randomString()}`;
      this.headsUpIdentifier = `heads-up_${randomString()}`;
    },

    async confirmDelete() {
      try {
        await this.$aiq.confirm(
          this.$t('global.delete'),
          this.$t(`${this.baseTranslationPath}.confirm_delete_message`), {
            cancelButtonText: this.$t('global.cancel'),
            confirmButtonText: this.$t('global.delete'),
          },
        );
        return true;
      } catch {
        return false;
      }
    },

    editNoteItem(note) {
      this.selectedNote = { ...note, is_heads_up: note.type === 'heads-up', expire_date: note.expire_at};
      this.isEditorVisible = true;
    },

    closeEditor() {
      this.isEditorVisible = false;
      this.selectedNote = null;
    },

    async onSearchChange(query) {
      this.selectedCollapse = ['standard', 'heads-up'];
      this.resetLists();
      await this.fetchNotes([0, query], [0, query]);
    },

    async onStandardScrollLoad($state) {
      await this.loadStandardNotes(this.standardNotes.length);
      if (this.standardNotes.length >= this.standardNotesPagination.rowCount) {
        $state.complete();
        return;
      }
      $state.loaded();
    },

    async onHeadsUpSrollLoad($state) {
      await this.loadHeadsUpNotes(this.headsUpNotes.length);
      if (this.headsUpNotes.length >= this.headsUpNotesPagination.rowCount) {
        $state.complete();
        return;
      }
      $state.loaded();
    },

    async loadStandardNotes(offset, query = '') {
      try {
        const params = {
          offset,
          limit: SEARCH_PAGE_SIZE,
          type: 'standard',
          query,
          include_expired: this.includeExpired || !this.featureFlags.NOTES_EXIPRATION,
        };
        const response = await this.$store.dispatch('conversations/getNotes', [this.conversation.id, params]);
        if (offset) {
          this.standardNotes = this.standardNotes.concat(get(response, 'data.models', []));
        } else {
          this.standardNotes = get(response, 'data.models', []);
        }
        this.standardNotesPagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
        return response;
      } catch {
        this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.fetch_failed`));
      }
    },

    async loadHeadsUpNotes(offset, query = '') {
      try {
        const params = {
          offset,
          limit: SEARCH_PAGE_SIZE,
          type: 'heads-up',
          query,
          include_expired: this.includeExpired || !this.featureFlags.NOTES_EXIPRATION,
        };
        const response = await this.$store.dispatch('conversations/getNotes', [this.conversation.id, params]);
        if (offset) {
          this.headsUpNotes = this.headsUpNotes.concat(get(response, 'data.models', []));
        } else {
          this.headsUpNotes = get(response, 'data.models', []);
        }
        this.headsUpNotesPagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
        return response;
      } catch {
        this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.fetch_failed`));
      }
    },

    async deleteNote(note) {
      try {
        const isConfirmed = await this.confirmDelete();
        if (isConfirmed) {
          await this.$store.dispatch('conversations/removeNote', [this.conversation.id, note.id]);
          this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.delete_success`));
          if (note.type === 'standard') {
            await this.loadStandardNotes();
          } else {
            await this.loadHeadsUpNotes();
          }
          // Need to update the count of all notes
          // We cannot do this through local notes because they also have search
          // so the total number of rows in the pagination object isn't the actual total
          await this.$store.dispatch('conversations/notesList', [this.conversation.id, 0, 0, true]);
        }
      } catch {
        this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.delete_failure`));
      }

      this.closeEditor();
    },

    async updateNote(newNoteItem) {
      const newContent = newNoteItem?.content ?? '';
      const newType = newNoteItem.is_heads_up ? 'heads-up' : 'standard';
      const expireDate = newNoteItem?.expire_date ?? null;
      if (!newNoteItem?.id) {
        // Add mode
        try {
          await this.$store.dispatch('conversations/addNote', [this.conversation.id, newContent, newType, expireDate]);
          this.resetLists();
          await this.fetchNotes();
          // Need to update the count of all notes
          // We cannot do this through local notes because they also have search
          // so the total number of rows in the pagination object isn't the actual total
          await this.$store.dispatch('conversations/notesList', [this.conversation.id, 0, 0, true]);
          this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.create_success`));
        } catch {
          this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.create_failure`));
        }
      }
      else {
        // Edit mode
        try {
          await this.$store.dispatch(
            'conversations/updateNote',
            [this.conversation.id, newNoteItem.id, { content: newContent, type: newType, expire_date: expireDate }],
          );
          this.resetLists();
          await this.fetchNotes();
          this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.update_success`));
        } catch {
          this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.update_failure`));
        }
      }

      this.closeEditor();
      this.selectedCollapse = [newType];
    },

    async createNote() {
      this.isEditorVisible = true;
    },
  },

  watch: {
    async includeExpired() {
      this.resetLists();
      await this.fetchNotes();
    },
  },
};
</script>

<style lang="scss" scoped>
  .notes_list > .el-select {
    margin-top: 10px;
    margin-bottom: 15px;
  }

  .button-add-new-item {
    flex: 0 0 0%;
    padding: 0 6px;
    color: #8492A6;
    font-size: 14px;
    border: 0;
    background-color: transparent;
  }
  .el-collapse.custom-notes {
    margin-top: 10px;
    border: unset;
    :deep {
      .el-collapse-item__header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding-left: 5px;
        background-color: unset;
        font-weight: 700;
      }
      .el-collapse-item__wrap {
        border: unset;
        margin-bottom: 10px;
        .el-collapse-item__content {
          max-height: 420px;
          overflow-y: auto;
          height: inherit;
          padding: 0px 5px 0px 5px;
        }
      }

    }
  }
  .text-center {
    text-align: center;
    margin-top: 5px;
  }

  .expired_toggler {
    margin-top: 10px;
    margin-bottom: 15px;
    display: flex;
    justify-content: space-between;
    padding-left: 5px;
    font-weight: 700;
    font-size: 13px;
  }

  .expire-date-row {
    display: flex;
    align-items: center;
    width: 100%;

    label {
      white-space: nowrap;
      flex: 1;
    }

    .date-picker-container{
      flex: 2;
    }

    :deep(.el-input) {
      &.el-date-editor {
        width: 220px;
      }
    }
  }
</style>
